// CookieBanner — P1-01 launcher cookie notice. Shown on the public launcher
// (App.jsx, pre-login) on a visitor's first visit, dismissed via a single OK
// button, suppressed on subsequent visits via a versioned localStorage flag.
//
// Scope: launcher only. The post-login PolicyAckModal in each portal is the
// authoritative acknowledgement and gates app use; this banner is the upstream
// notice for any visitor who lands on the launcher and doesn't proceed to a
// portal (UK ePrivacy / ICO posture — inform on first visit even for
// strictly-necessary storage).
//
// localStorage key versioning: `sykes_cookie_notice_dismissed_v1`. Bump the
// `_v1` suffix in BOTH this constant and the storage key when the notice
// text materially changes, to force a re-show.
import React, { useState, useEffect } from 'react';

const DISMISSED_KEY = 'sykes_cookie_notice_dismissed_v1';

export function CookieBanner() {
  const [show, setShow] = useState(false);

  // Render decision happens after mount so SSR / first paint doesn't briefly
  // show the banner to returning visitors. Reads localStorage in an effect to
  // avoid hydration mismatch in case this is ever moved to an SSR context.
  useEffect(() => {
    try {
      if (!localStorage.getItem(DISMISSED_KEY)) setShow(true);
    } catch (_) {
      // Private mode or storage disabled — show the banner; dismiss won't
      // persist but a visible notice is better than a silent failure.
      setShow(true);
    }
  }, []);

  if (!show) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, new Date().toISOString()); }
    catch (_) { /* see above */ }
    setShow(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie and data storage notice"
      style={{
        position:'fixed', left:0, right:0, bottom:0, zIndex:10500,
        background:'rgba(15,23,42,.95)', borderTop:'1px solid rgba(255,255,255,.12)',
        backdropFilter:'blur(8px)', padding:'14px 20px',
        display:'flex', alignItems:'center', justifyContent:'center', gap:16,
        flexWrap:'wrap', fontFamily:'"DM Sans",-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
      }}
    >
      <div style={{flex:'1 1 480px', maxWidth:780, color:'#e2e8f0', fontSize:13, lineHeight:1.55}}>
        This site stores small amounts of strictly necessary data on your device
        to keep you signed in and to protect your session. We do <strong>not</strong> use
        cookies, analytics, or tracking. See <strong>Policy 40</strong> (available from your
        administrator on request) for details.
      </div>
      <button
        onClick={dismiss}
        style={{
          padding:'10px 28px', borderRadius:8, border:'none',
          background:'linear-gradient(135deg,#0ea5e9,#0284c7)', color:'#fff',
          fontSize:13, fontWeight:700, letterSpacing:.5, cursor:'pointer',
          boxShadow:'0 4px 12px rgba(14,165,233,.3)',
        }}
      >
        OK
      </button>
    </div>
  );
}

export default CookieBanner;
